import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  CalendarOff,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Calendar,
  User
} from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { fmtTz } from "../lib/time";

const REQUEST_TYPES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'other', label: 'Other' }
];

export default function AdminTimeOff() {
  const { authAxios, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionNote, setActionNote] = useState('');

  const isAdmin = user?.role === 'admin';

  const [formData, setFormData] = useState({
    request_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const fetchRequests = useCallback(async () => {
    try {
      const res = await authAxios.get('/time-off');
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load time off requests');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const resetForm = () => {
    setFormData({
      request_type: 'vacation',
      start_date: '',
      end_date: '',
      reason: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await authAxios.post('/time-off', formData);
      toast.success('Time off request submitted');
      setDialogOpen(false);
      resetForm();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await authAxios.post(`/time-off/${requestId}/action`, {
        action,
        note: actionNote
      });
      toast.success(`Request ${action}d`);
      setSelectedRequest(null);
      setActionNote('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      denied: 'bg-rose-100 text-rose-700 border-rose-200'
    };
    const icons = {
      pending: <Clock className="w-3 h-3 mr-1" />,
      approved: <CheckCircle className="w-3 h-3 mr-1" />,
      denied: <XCircle className="w-3 h-3 mr-1" />
    };
    return (
      <Badge variant="outline" className={`${styles[status]} flex items-center`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      vacation: 'bg-blue-100 text-blue-700 border-blue-200',
      sick: 'bg-orange-100 text-orange-700 border-orange-200',
      unpaid: 'bg-purple-100 text-purple-700 border-purple-200',
      other: 'bg-stone-100 text-stone-700 border-stone-200'
    };
    return (
      <Badge variant="outline" className={colors[type]}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === 'all') return true;
    return r.status === activeTab;
  });

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    denied: requests.filter(r => r.status === 'denied').length
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-time-off">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Time Off Requests</h1>
          <p className="text-stone-500 mt-1">{isAdmin ? 'Manage employee time off requests' : 'View and submit time off requests'}</p>
        </div>
        {!isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="request-time-off-btn">
                <Plus className="w-5 h-5 mr-2" />
                Request Time Off
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CalendarOff className="w-5 h-5 text-emerald-600" />
                  Request Time Off
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Request Type *</Label>
                  <Select value={formData.request_type} onValueChange={(v) => setFormData({ ...formData, request_type: v })}>
                    <SelectTrigger data-testid="time-off-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REQUEST_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      data-testid="time-off-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      data-testid="time-off-end-date"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Textarea
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Optional - explain your request..."
                    data-testid="time-off-reason"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 w-full" data-testid="submit-time-off-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Submit Request
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-stone-100 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="all" className="data-[state=active]:bg-white">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-white">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="denied" className="data-[state=active]:bg-white">Denied ({counts.denied})</TabsTrigger>
        </TabsList>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-stone-400" />
              Time Off Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <CalendarOff className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No time off requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-100">
                      {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Employee</TableHead>}
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Dates</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Days</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Reason</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                      {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((req) => (
                      <TableRow key={req.id} className="border-stone-100 hover:bg-stone-50">
                        {isAdmin && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold text-xs">
                                {req.employee_name?.split(' ').map(n => n[0]).join('')}
                              </div>
                              <span className="font-medium">{req.employee_name}</span>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>{getTypeBadge(req.request_type)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{fmtTz(req.start_date, 'MMM d, yyyy')}</div>
                            <div className="text-stone-500">to {fmtTz(req.end_date, 'MMM d, yyyy')}</div>
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">{req.total_days}</TableCell>
                        <TableCell className="text-stone-600 max-w-[200px] truncate">{req.reason || '-'}</TableCell>
                        <TableCell>
                          {getStatusBadge(req.status)}
                          {req.conflicts?.length > 0 && (
                            <div className="flex items-center gap-1 text-amber-600 text-xs mt-1">
                              <AlertTriangle className="w-3 h-3" />
                              {req.conflicts.length} conflict(s)
                            </div>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            {req.status === 'pending' && (
                              <div className="flex items-center gap-1 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAction(req.id, 'approve')}
                                  disabled={processingId === req.id}
                                  className="text-emerald-600 hover:bg-emerald-50"
                                  data-testid={`approve-${req.id}`}
                                >
                                  {processingId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                  Approve
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAction(req.id, 'deny')}
                                  disabled={processingId === req.id}
                                  className="text-rose-600 hover:bg-rose-50"
                                  data-testid={`deny-${req.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Deny
                                </Button>
                              </div>
                            )}
                            {req.status !== 'pending' && req.reviewed_by_name && (
                              <div className="text-xs text-stone-500">
                                by {req.reviewed_by_name}
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
